import React, { MouseEvent, useCallback } from "react";

import { faCheckCircle } from "@fortawesome/pro-solid-svg-icons/faCheckCircle";
import { faEyeSlash } from "@fortawesome/pro-solid-svg-icons/faEyeSlash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";

import { Lead } from "@mapmycustomers/shared/types/entity";
import { DuplicateEntity } from "@mapmycustomers/shared/types/entity/Duplicate";
import { stopEvents } from "@mapmycustomers/shared/util/browser";

import DuplicateWarningIcon from "@app/component/DuplicateWarningIcon";
import useMap from "@app/component/map/FeaturedMap/context/useMap";
import { Item } from "@app/component/RecordCard";
import colors from "@app/styles/_colors.module.scss";
import { leadIdGetter } from "@app/util/map/idGetters";

import LeadFinderItemActions from "./components/LeadFinderItemActions";
import LeadItemHours from "./components/LeadItemHours";
import styles from "./LeadItem.module.scss";

interface LeadItemProps {
  className?: string;
  entity: Lead;
  onSetAnnotation?: (placeId: Lead["place_id"] | undefined) => void;
  onWarningClick?: (data: { primaryRecord?: Lead; records?: DuplicateEntity[] }) => void;
  selected?: boolean;
  showActions?: boolean;
  showSelectLead?: boolean;
  toggleSelectedItem?: (data: { entity: string; value?: boolean | undefined }) => void;
}

const LeadItem = ({
  className,
  entity,
  onSetAnnotation,
  onWarningClick,
  selected,
  showActions = true,
  showSelectLead = true,
  toggleSelectedItem,
}: LeadItemProps) => {
  const { map } = useMap();

  const handleCheckboxClick = useCallback(
    (ev: MouseEvent) => {
      ev.stopPropagation();
      toggleSelectedItem?.({ entity: leadIdGetter(entity) });
    },
    [entity, toggleSelectedItem]
  );

  const handleClick = useCallback(() => {
    onSetAnnotation?.(entity.place_id);
    map.panTo(entity.geometry.location);
  }, [entity, map, onSetAnnotation]);

  const handleActionsClick = useCallback((e: MouseEvent) => {
    // prevent clicking on actions to be treated as a LeadItem click
    // but allow A tag to proceed (that's why no e.preventDefault())
    e.stopPropagation();
  }, []);

  return (
    <Item
      actions={
        showActions ? (
          <div className={styles.actions} onClick={handleActionsClick}>
            <LeadFinderItemActions disableEmpty entity={entity} />
            <DuplicateWarningIcon entity={entity} onClick={onWarningClick} />
          </div>
        ) : undefined
      }
      borderColor={selected ? colors.primary100 : colors.primary050}
      className={className}
      entity={entity}
      icon={
        showSelectLead ? (
          <div
            className={cn(styles.selection, { [styles.active]: selected })}
            onClick={handleCheckboxClick}
          >
            {selected ? <FontAwesomeIcon icon={faCheckCircle} /> : <span />}
          </div>
        ) : (
          <DuplicateWarningIcon disable entity={entity} onClick={onWarningClick} />
        )
      }
      name={
        <span className={styles.name}>
          {entity.blackListed && <FontAwesomeIcon icon={faEyeSlash} />}
          {entity.name}
        </span>
      }
      onClick={handleClick}
    >
      <div className={styles.address}>{entity.formatted_address}</div>
      <div
        // prevent clicking on open hours collapsable to be treated as a LeadItem click
        onClick={stopEvents}
      >
        <LeadItemHours openingHours={entity.opening_hours} />
      </div>
    </Item>
  );
};

export default LeadItem;
